import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "track",
    "slide",
    "counter",
    "previous",
    "next",
    "thumbnail",
    "lightbox",
    "lightboxImage",
    "lightboxCounter",
    "lightboxDownload",
    "strip",
    "stripPrevious",
    "stripNext",
  ]

  connect() {
    this.index = 0
    this.pointerStart = null
    this.dragged = false
    this.onResize = () => this.updateStripNavigation()
    window.addEventListener("resize", this.onResize)
    this.show(0, { scrollThumbnail: false })
  }

  disconnect() {
    window.removeEventListener("resize", this.onResize)
  }

  get count() {
    return this.slideTargets.length
  }

  previous() {
    this.show(this.index - 1)
  }

  next() {
    this.show(this.index + 1)
  }

  select(event) {
    const index = this.thumbnailTargets.indexOf(event.currentTarget)

    if (index !== -1) {
      this.show(index)
    }
  }

  openLightbox(event) {
    if (this.dragged) return
    if (event.target.closest("a, button")) return
    if (!this.hasLightboxTarget || this.lightboxTarget.open) return

    this.syncLightbox()
    this.lightboxTarget.showModal()
  }

  closeLightbox() {
    if (this.hasLightboxTarget && this.lightboxTarget.open) {
      this.lightboxTarget.close()
    }
  }

  lightboxClick(event) {
    if (event.target.closest("button, a, img")) return

    this.closeLightbox()
  }

  pointerDown(event) {
    this.pointerStart = event.clientX
    this.dragged = false
  }

  pointerCancel() {
    this.pointerStart = null
  }

  pointerUp(event) {
    if (this.pointerStart === null) return

    const delta = event.clientX - this.pointerStart
    this.pointerStart = null

    if (Math.abs(delta) < 40) return

    this.dragged = true

    if (delta < 0) {
      this.next()
    } else {
      this.previous()
    }
  }

  scrollThumbnails(event) {
    if (!this.hasStripTarget) return

    const direction = Number(event.currentTarget.dataset.direction) || 1

    this.stripTarget.scrollBy({
      left: direction * this.stripTarget.clientWidth * 0.8,
      behavior: "smooth",
    })
  }

  updateStripNavigation() {
    if (!this.hasStripTarget) return

    const strip = this.stripTarget
    const maxScroll = strip.scrollWidth - strip.clientWidth
    const scrollable = maxScroll > 1

    this.stripPreviousTargets.forEach((button) => {
      button.hidden = !scrollable
      button.disabled = !scrollable || strip.scrollLeft <= 1
    })

    this.stripNextTargets.forEach((button) => {
      button.hidden = !scrollable
      button.disabled = !scrollable || strip.scrollLeft >= maxScroll - 1
    })
  }

  show(index, { scrollThumbnail = true } = {}) {
    if (this.count === 0) return

    this.index = Math.max(0, Math.min(index, this.count - 1))
    this.trackTarget.style.transform = `translateX(-${this.index * 100}%)`

    this.slideTargets.forEach((slide, i) => {
      const active = i === this.index
      slide.setAttribute("aria-hidden", active ? "false" : "true")

      if (active) {
        slide.removeAttribute("inert")
      } else {
        slide.setAttribute("inert", "")
      }
    })

    this.thumbnailTargets.forEach((thumbnail, i) => {
      const active = i === this.index

      if (active) {
        thumbnail.dataset.active = "true"
        thumbnail.setAttribute("aria-current", "true")

        if (scrollThumbnail) {
          thumbnail.scrollIntoView({ block: "nearest", inline: "nearest" })
        }
      } else {
        delete thumbnail.dataset.active
        thumbnail.removeAttribute("aria-current")
      }
    })

    if (this.hasCounterTarget) {
      this.counterTarget.textContent = `${this.index + 1} / ${this.count}`
    }

    this.updateNavigation()
    this.updateStripNavigation()

    if (this.hasLightboxTarget && this.lightboxTarget.open) {
      this.syncLightbox()
    }
  }

  updateNavigation() {
    this.previousTargets.forEach((button) => {
      button.disabled = this.index === 0
    })

    this.nextTargets.forEach((button) => {
      button.disabled = this.index === this.count - 1
    })
  }

  syncLightbox() {
    if (!this.hasLightboxImageTarget) return

    const slide = this.slideTargets[this.index]
    const image = slide?.querySelector("img")

    if (image) {
      this.lightboxImageTarget.src =
        slide.dataset.fullSrc || image.currentSrc || image.src
      this.lightboxImageTarget.alt = image.alt || ""
    }

    if (this.hasLightboxCounterTarget) {
      this.lightboxCounterTarget.textContent = `${this.index + 1} / ${this.count}`
    }

    if (this.hasLightboxDownloadTarget && slide?.dataset.downloadUrl) {
      this.lightboxDownloadTarget.href = slide.dataset.downloadUrl
      this.lightboxDownloadTarget.setAttribute(
        "aria-label",
        `Download image ${this.index + 1} at full resolution`
      )
    }

    this.updateNavigation()
  }
}
